import { useCallback, useEffect, useRef, useState } from "react"

import {
  type SmsVerificationState,
  getSmsVerification
} from "./sms-verification.services.ts"

export type SmsLock =
  | "unknown"
  | "unavailable"
  | "unverified"
  | "stopped"
  | null

export const smsLockOf = (state: SmsVerificationState | null): SmsLock => {
  if (!state) return "unknown"
  if (!state.available) return "unavailable"
  if (state.stopped) return "stopped"
  return state.verified ? null : "unverified"
}

/** The signed-in user's SMS verification state, read from the server and
 *  re-read on demand; a newer read always wins over an older one. */
export function useSmsVerification() {
  const [state, setState] = useState<SmsVerificationState | null>(null)
  const [failed, setFailed] = useState(false)
  const latest = useRef(0)

  const reload = useCallback(() => {
    const ask = ++latest.current
    return getSmsVerification().then((response) => {
      if (ask !== latest.current) return
      if (response.ok && response.data) {
        setState(response.data)
        setFailed(false)
      } else {
        setFailed(true)
      }
    })
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  return { state, failed, reload, setState, lock: smsLockOf(state) }
}
